import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Typography, TextField, Button, Box, Grid, FormControlLabel, Switch, CircularProgress, Tabs, Tab, Select, MenuItem, FormControl, InputLabel, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import LocalidadSearch from '../../components/LocalidadSearch';
import localidadService from '../../services/localidadService';
import RutaSearch from '../../components/RutaSearch';
import rutaService, { Ruta } from '../../services/rutaService';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';
import ramoProvService, { RamoProv } from '../../services/ramoProvService';
import estrategiaService from '../../services/estrategiaService';

const ProveedorForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroProveedor } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [openLocalidadSearch, setOpenLocalidadSearch] = useState(false);
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState<{ codigoCiudad: number; nombreCiudad?: string; nombreProvincia?: string } | null>(null);
  const [openRutaSearch, setOpenRutaSearch] = useState(false);
  const [rutaSeleccionada, setRutaSeleccionada] = useState<Pick<Ruta, 'codigoRuta' | 'nombreRuta'> | null>(null);
  const [openImputableSearch, setOpenImputableSearch] = useState(false);
  const [imputableSeleccionado, setImputableSeleccionado] = useState<Pick<Imputable, 'nroCuenta' | 'nomCuenta'> | null>(null);
  const [openImputableComprasSearch, setOpenImputableComprasSearch] = useState(false);
  const [imputableComprasSeleccionado, setImputableComprasSeleccionado] = useState<Pick<Imputable, 'nroCuenta' | 'nomCuenta'> | null>(null);
  const [ramosProv, setRamosProv] = useState<RamoProv[]>([]);
  const [openRamoProvDialog, setOpenRamoProvDialog] = useState(false);
  const [ramoProvSeleccionado, setRamoProvSeleccionado] = useState<string>('');
  const [formData, setFormData] = useState<Proveedor>({
    nroProveedor: 0,
    nombre: '',
    direccion: '',
    codigoCiudad: undefined,
    telefono1: '',
    telefono2: '',
    fax: '',
    email: '',
    tipoIva: '',
    cuit: '',
    dni: undefined as unknown as number,
    contacto: '',
    descuento: undefined,
    nroCuenta: undefined,
    fechaAlta: undefined,
    fechaBaja: undefined,
    plazoEntrega: undefined,
    codigoRuta: undefined,
    saldo: undefined,
    condVta: 'CTA.CTE.',
    tipoProveedor: '',
    notas: '',
    activo: 'A' as any,
    sitIB: '',
    esPersHumSucIndig: false,
    nroCuentaCompras: undefined,
    codigoRamoProv: ''
  });

  const cleanString = (value?: string | null) => {
    if (value === null || value === undefined) return '';
    return `${value}`.trim();
  };

  const loadProveedor = useCallback(async () => {
    if (!nroProveedor) return;
    try {
      setLoading(true);
      const prov = await proveedorService.getProveedor(Number(nroProveedor));
      // Convertir activo - puede venir como boolean, string "I", "A", "E", "S", o con #
      let activoValue = 'A';
      if (typeof prov.activo === 'boolean') {
        activoValue = prov.activo ? 'A' : 'I';
      } else if (prov.activo) {
        const activoStr = String(prov.activo).trim();
        // Remover # si existe y normalizar
        const activoNormalizado = activoStr.startsWith('#') ? activoStr.substring(1) : activoStr;
        const activoMap: { [key: string]: string } = { 'A': 'A', 'I': 'I', 'E': 'E', 'S': 'S', 'N': 'I' };
        activoValue = activoMap[activoNormalizado.toUpperCase()] || 'A';
      }
      
      // Manejar condVta - puede venir como condVta, CondVta, o cualquier variante, y limpiar espacios
      const condVtaFromApi = (prov as any).condVta || (prov as any).CondVta || prov.condVta || '';
      const condVtaValue = cleanString(condVtaFromApi) || 'CTA.CTE.';
      
      // Manejar tipoProveedor - puede venir como tipoProveedor, TipoProveedor, y normalizar "C" o "G"
      const tipoProveedorFromApi = (prov as any).tipoProveedor || (prov as any).TipoProveedor || prov.tipoProveedor || '';
      let tipoProveedorValue = cleanString(tipoProveedorFromApi);
      // Remover # si existe y normalizar
      if (tipoProveedorValue) {
        tipoProveedorValue = tipoProveedorValue.startsWith('#') ? tipoProveedorValue.substring(1) : tipoProveedorValue;
        tipoProveedorValue = tipoProveedorValue.trim().toUpperCase();
        if (tipoProveedorValue !== 'C' && tipoProveedorValue !== 'G') {
          tipoProveedorValue = '';
        }
      }
      
      // Manejar sitIB - puede venir como sitIB o sitIg, normalizar a "1" o "2"
      const sitIBFromApi = (prov as any).sitIB || (prov as any).sitIg || (prov as any).SitIB || (prov as any).SitIg || '';
      let sitIBValue = cleanString(sitIBFromApi);
      // Remover # si existe y normalizar
      if (sitIBValue) {
        sitIBValue = sitIBValue.startsWith('#') ? sitIBValue.substring(1) : sitIBValue;
        sitIBValue = sitIBValue.trim();
        if (sitIBValue !== '1' && sitIBValue !== '2') {
          sitIBValue = '';
        }
      }
      
      // Manejar esPersHumSucIndig - puede venir como esPersHumSucIndig o esPersHumSucIndIg
      const esPersHumSucIndigFromApi = (prov as any).esPersHumSucIndig !== undefined 
        ? (prov as any).esPersHumSucIndig 
        : ((prov as any).esPersHumSucIndIg !== undefined ? (prov as any).esPersHumSucIndIg : prov.esPersHumSucIndig);
      const esPersHumSucIndigValue = esPersHumSucIndigFromApi !== null && esPersHumSucIndigFromApi !== undefined 
        ? Boolean(esPersHumSucIndigFromApi) 
        : false;
      
      setFormData({ 
        ...prov, 
        activo: activoValue as any, 
        condVta: condVtaValue, 
        tipoProveedor: tipoProveedorValue,
        sitIB: sitIBValue,
        esPersHumSucIndig: esPersHumSucIndigValue
      });
      
      // Cargar localidad si existe
      setLocalidadSeleccionada(null);
      if (prov.codigoCiudad) {
        try {
          const localidad = await localidadService.getLocalidad(prov.codigoCiudad);
          setLocalidadSeleccionada({
            codigoCiudad: localidad.codigoCiudad,
            nombreCiudad: cleanString(localidad.nombreCiudad),
            nombreProvincia: cleanString(localidad.nombreProvincia),
          });
        } catch (error) {
          console.error('Error al cargar localidad:', error);
        }
      }
      
      // Cargar ruta si existe
      setRutaSeleccionada(null);
      if (prov.codigoRuta) {
        try {
          const ruta = await rutaService.getRuta(prov.codigoRuta);
          if (ruta) {
            setRutaSeleccionada({
              codigoRuta: ruta.codigoRuta,
              nombreRuta: cleanString(ruta.nombreRuta),
            });
          }
        } catch (error) {
          console.error('Error al cargar ruta:', error);
        }
      }
      
      // Cargar cuenta contable (Imputable) si existe
      setImputableSeleccionado(null);
      if (prov.nroCuenta) {
        try {
          const imputable = await imputableService.getImputable(prov.nroCuenta);
          if (imputable) {
            setImputableSeleccionado({
              nroCuenta: imputable.nroCuenta,
              nomCuenta: cleanString(imputable.nomCuenta),
            });
          }
        } catch (error) {
          console.error('Error al cargar cuenta contable:', error);
        }
      }
      
      // Cargar cuenta contable de compras (Imputable) si existe
      setImputableComprasSeleccionado(null);
      if (prov.nroCuentaCompras) {
        try {
          const imputable = await imputableService.getImputable(prov.nroCuentaCompras);
          if (imputable) {
            setImputableComprasSeleccionado({
              nroCuenta: imputable.nroCuenta,
              nomCuenta: cleanString(imputable.nomCuenta),
            });
          }
        } catch (error) {
          console.error('Error al cargar cuenta contable de compras:', error);
        }
      }
    } catch (error) {
      console.error('Error al cargar el proveedor:', error);
      enqueueSnackbar('Error al cargar el proveedor', { variant: 'error' });
      navigate('/compras/proveedores');
    } finally {
      setLoading(false);
    }
  }, [nroProveedor, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroProveedor) {
      loadProveedor();
    } else {
      // Para nuevos proveedores, obtener próximo número de proveedor
      proveedorService.getProximoNumero().then(proximoNumero => {
        setFormData(prev => ({
          ...prev,
          nroProveedor: proximoNumero
        }));
      }).catch(() => {
        // Si no se puede obtener el próximo número, usar valores por defecto
        console.error('Error al obtener el próximo número de proveedor');
      });
      
      // Cargar estrategia para obtener cuenta contable sugerida
      estrategiaService.getEstrategia(1).then((estrategia: any) => {
        if (estrategia?.proveedores) {
          const nroCuenta = estrategia.proveedores ?? undefined;
          setFormData(prev => ({
            ...prev,
            nroCuenta: nroCuenta
          }));
          // Cargar información de la cuenta contable
          if (nroCuenta) {
            imputableService.getImputable(nroCuenta).then(imputable => {
              if (imputable) {
                setImputableSeleccionado({
                  nroCuenta: imputable.nroCuenta,
                  nomCuenta: cleanString(imputable.nomCuenta),
                });
              }
            }).catch(() => {
              // Si no se puede cargar, no es crítico
            });
          }
        }
      }).catch(() => {
        // Si no se puede cargar la estrategia, no es crítico
      });
    }
  }, [nroProveedor, loadProveedor]);

  // Cargar ramos de proveedores al montar el componente
  useEffect(() => {
    const loadRamosProv = async () => {
      try {
        const ramos = await ramoProvService.getRamosProv();
        setRamosProv(ramos);
      } catch (error) {
        console.error('Error al cargar ramos de proveedores:', error);
        enqueueSnackbar('Error al cargar ramos de proveedores', { variant: 'error' });
      }
    };
    loadRamosProv();
  }, [enqueueSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set(['nroProveedor','dni','codigoCiudad','descuento','nroCuenta','plazoEntrega','codigoRuta','nroCuentaCompras']);
    if (numericFields.has(name)) {
      const parsed = value === '' ? (undefined as unknown as number) : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed } as any));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSelectLocalidad = (localidad: { codigoCiudad: number; nombreCiudad?: string; nombreProvincia?: string }) => {
    setLocalidadSeleccionada(localidad);
    setFormData(prev => ({ ...prev, codigoCiudad: localidad.codigoCiudad }));
  };

  const handleSelectRuta = (ruta: Ruta) => {
    setRutaSeleccionada({
      codigoRuta: ruta.codigoRuta,
      nombreRuta: cleanString(ruta.nombreRuta),
    });
    setFormData(prev => ({ ...prev, codigoRuta: ruta.codigoRuta ?? undefined }));
  };

  const handleSelectImputable = (imputable: Imputable) => {
    setImputableSeleccionado({
      nroCuenta: imputable.nroCuenta,
      nomCuenta: cleanString(imputable.nomCuenta),
    });
    setFormData(prev => ({ ...prev, nroCuenta: imputable.nroCuenta }));
  };

  const handleSelectImputableCompras = (imputable: Imputable) => {
    setImputableComprasSeleccionado({
      nroCuenta: imputable.nroCuenta,
      nomCuenta: cleanString(imputable.nomCuenta),
    });
    setFormData(prev => ({ ...prev, nroCuentaCompras: imputable.nroCuenta }));
  };

  const handleConfirmarRamoProv = async (heredar: boolean) => {
    setOpenRamoProvDialog(false);
    
    // Actualizar el código ramo prov
    setFormData(prev => ({ ...prev, codigoRamoProv: ramoProvSeleccionado }));
    
    if (heredar) {
      // Buscar el ramo prov seleccionado en la lista
      const ramoSeleccionado = ramosProv.find(
        ramo => cleanString(ramo.codigoramoprov) === ramoProvSeleccionado
      );
      
      if (ramoSeleccionado) {
        // Heredar N° Cuenta (ctactasxpg)
        if (ramoSeleccionado.ctactasxpg) {
          try {
            const imputable = await imputableService.getImputable(ramoSeleccionado.ctactasxpg);
            if (imputable) {
              setImputableSeleccionado({
                nroCuenta: imputable.nroCuenta,
                nomCuenta: cleanString(imputable.nomCuenta),
              });
              setFormData(prev => ({ ...prev, nroCuenta: imputable.nroCuenta }));
            }
          } catch (error) {
            console.error('Error al cargar cuenta contable del ramo prov:', error);
            enqueueSnackbar('Error al cargar la cuenta contable del ramo prov', { variant: 'warning' });
          }
        }
        
        // Heredar N° Cuenta Compras (ctacompras)
        if (ramoSeleccionado.ctacompras) {
          try {
            const imputable = await imputableService.getImputable(ramoSeleccionado.ctacompras);
            if (imputable) {
              setImputableComprasSeleccionado({
                nroCuenta: imputable.nroCuenta,
                nomCuenta: cleanString(imputable.nomCuenta),
              });
              setFormData(prev => ({ ...prev, nroCuentaCompras: imputable.nroCuenta }));
            }
          } catch (error) {
            console.error('Error al cargar cuenta de compras del ramo prov:', error);
            enqueueSnackbar('Error al cargar la cuenta de compras del ramo prov', { variant: 'warning' });
          }
        }
        
        enqueueSnackbar('Números de cuenta heredados del ramo prov', { variant: 'success' });
      }
    }
    
    setRamoProvSeleccionado('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload: Proveedor = {
        ...formData,
        nroProveedor: Number(formData.nroProveedor) || 0,
      };
      // Validación de obligatorios: permitir otros vacíos, pero avisar/bloquear si faltan estos
      const faltantes: string[] = [];
      if (!payload.nroProveedor || isNaN(payload.nroProveedor) || payload.nroProveedor <= 0) faltantes.push('nroProveedor');
      if (!String(formData.nombre || '').trim()) faltantes.push('nombre');
      if (!String(formData.direccion || '').trim()) faltantes.push('direccion');
      if (!String(formData.tipoProveedor || '').trim()) faltantes.push('tipoProveedor');
      if (faltantes.length > 0) {
        enqueueSnackbar(`Complete los campos obligatorios: ${faltantes.join(', ')}` , { variant: 'warning' });
        setSaving(false);
        return;
      }
      // Validación de tipos según API
      const numericFields = ['nroProveedor','dni','codigoCiudad','descuento','nroCuenta','plazoEntrega','codigoRuta','nroCuentaCompras'];
      const dateFields = ['fechaAlta','fechaBaja'];
      const invalidNumeric: string[] = [];
      for (const f of numericFields) {
        const v: any = (formData as any)[f];
        if (v !== undefined && v !== null && v !== '' && (typeof v !== 'number' || isNaN(Number(v)))) {
          invalidNumeric.push(f);
        }
      }
      const invalidDates: string[] = [];
      for (const f of dateFields) {
        const v: any = (formData as any)[f];
        if (v) {
          const d = new Date(v);
          if (isNaN(d.getTime())) invalidDates.push(f);
        }
      }
      const email = String(formData.email || '').trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      const emailInvalido = email !== '' && !emailRegex.test(email);
      if (invalidNumeric.length || invalidDates.length || emailInvalido) {
        if (invalidNumeric.length) enqueueSnackbar(`Los siguientes campos deben ser numéricos: ${invalidNumeric.join(', ')}`, { variant: 'error', autoHideDuration: 9000 });
        if (invalidDates.length) enqueueSnackbar(`Formato de fecha inválido en: ${invalidDates.join(', ')}`, { variant: 'error', autoHideDuration: 9000 });
        if (emailInvalido) enqueueSnackbar('El email tiene formato inválido. Ej: nombre@dominio.com', { variant: 'error', autoHideDuration: 9000 });
        setSaving(false);
        return;
      }
      // Transformaciones de tipos esperados por el backend
      const toIso = (v: any) => {
        if (!v) return null;
        if (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(v + 'T00:00:00Z').toISOString();
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d.toISOString();
      };
      const apiPayload: any = {
        ...payload,
        // normalizar strings obligatorios
        nombre: String(formData.nombre || '').trim(),
        direccion: String(formData.direccion || '').trim(),
        tipoProveedor: String(formData.tipoProveedor || '').trim(),
        // activo es un string con valores A, I, E, S
        activo: String(formData.activo || 'A').trim(),
        esPersHumSucIndig: formData.esPersHumSucIndig ? 1 : 0,
        fechaAlta: toIso(formData.fechaAlta),
        fechaBaja: toIso(formData.fechaBaja)
      };
      // Compatibilidad por si el backend requiere PascalCase
      apiPayload.Nombre = apiPayload.nombre;
      apiPayload.Direccion = apiPayload.direccion;
      apiPayload.TipoProveedor = apiPayload.tipoProveedor;
      if (nroProveedor) {
        await proveedorService.updateProveedor(Number(nroProveedor), apiPayload as any);
      } else {
        await proveedorService.createProveedor(apiPayload as any);
      }
      enqueueSnackbar('Proveedor guardado exitosamente', { variant: 'success' });
      navigate('/compras/proveedores');
    } catch (error: any) {
      // Manejo detallado de errores desde backend: campos, duplicados, tipos inválidos
      let mostrado = false;
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      const messageText = (data?.message || data || error?.message || '').toString();
      if (!mostrado && messageText) {
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`El proveedor con código ${formData.nroProveedor} ya existe.`, { variant: 'error' });
          mostrado = true;
        }
      }
      if (!mostrado && messageText) {
        if (messageText.toLowerCase().includes('invalid') || messageText.toLowerCase().includes('format') || messageText.toLowerCase().includes('tipo')) {
          enqueueSnackbar(`Algunos datos tienen tipo o formato inválido. Detalle: ${messageText.slice(0,120)}`, { variant: 'error', autoHideDuration: 10000 });
          mostrado = true;
        }
      }
      if (!mostrado) {
        enqueueSnackbar('Error al guardar el proveedor', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{
            fontWeight: 600,
            color: '#1976d2',
            mb: 3
          }}
        >
          {nroProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="pestañas de proveedor"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  '&.Mui-selected': {
                    color: '#1976d2',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              <Tab label="General" />
              <Tab label="Datos de Contacto" />
              <Tab label="Comercial" />
              <Tab label="Extras" />
            </Tabs>
          </Box>

          {/* Pestaña General */}
          {currentTab === 0 && (
            <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                name="nroProveedor"
                label="N° Proveedor"
                type="number"
                value={formData.nroProveedor}
                onChange={handleChange}
                fullWidth
                required
                disabled={!!nroProveedor}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField
                name="nombre"
                label="Nombre"
                value={formData.nombre}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="cuit" 
                label="CUIT" 
                value={formData.cuit || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="dni" 
                label="DNI" 
                type="number" 
                value={formData.dni ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo IVA</InputLabel>
                <Select
                  name="tipoIva"
                  value={formData.tipoIva || ''}
                  label="Tipo IVA"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoIva: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="I">Responsable Inscripto</MenuItem>
                  <MenuItem value="M">Monotributo</MenuItem>
                  <MenuItem value="E">Exento</MenuItem>
                  <MenuItem value="P">Presupuestos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo de Proveedor</InputLabel>
                <Select
                  name="tipoProveedor"
                  value={cleanString(formData.tipoProveedor) || ''}
                  label="Tipo de Proveedor"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoProveedor: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="C">Compras</MenuItem>
                  <MenuItem value="G">Gastos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Estado</InputLabel>
                <Select
                  name="activo"
                  value={formData.activo || 'A'}
                  label="Estado"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.value as any }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="A">Activo</MenuItem>
                  <MenuItem value="I">Inactivo</MenuItem>
                  <MenuItem value="E">Eventual</MenuItem>
                  <MenuItem value="S">Suspendido</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={9}>
              {/* Espacio vacío para forzar nueva fila */}
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Código Ramo Prov.</InputLabel>
                <Select
                  name="codigoRamoProv"
                  value={cleanString(formData.codigoRamoProv) || ''}
                  label="Código Ramo Prov."
                  onChange={(e) => {
                    const nuevoCodigo = e.target.value;
                    if (nuevoCodigo && nuevoCodigo !== cleanString(formData.codigoRamoProv)) {
                      setRamoProvSeleccionado(nuevoCodigo);
                      setOpenRamoProvDialog(true);
                    } else {
                      setFormData(prev => ({ ...prev, codigoRamoProv: nuevoCodigo }));
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  {ramosProv.map((ramo) => {
                    const codigo = cleanString(ramo.codigoramoprov);
                    const nombre = cleanString(ramo.nombreramoprov);
                    return (
                      <MenuItem key={codigo} value={codigo}>
                        {codigo} - {nombre}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="nroCuenta"
                label="Nro Cuenta - Ctas x Pagar"
                value={imputableSeleccionado ? `${imputableSeleccionado.nroCuenta} - ${imputableSeleccionado.nomCuenta?.trim() || ''}` : (formData.nroCuenta || '')}
                fullWidth 
                required
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenImputableSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="nroCuentaCompras"
                label="Nro Cuenta - Compras"
                value={imputableComprasSeleccionado ? `${imputableComprasSeleccionado.nroCuenta} - ${imputableComprasSeleccionado.nomCuenta?.trim() || ''}` : (formData.nroCuentaCompras || '')}
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenImputableComprasSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            </Grid>
          )}

          {/* Pestaña Datos de Contacto */}
          {currentTab === 1 && (
            <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField 
                name="direccion" 
                label="Dirección" 
                value={formData.direccion || ''} 
                onChange={handleChange} 
                fullWidth 
                required 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                name="codigoCiudad"
                label="Localidad"
                value={localidadSeleccionada 
                  ? `${localidadSeleccionada.codigoCiudad}${localidadSeleccionada.nombreCiudad ? ' - ' + localidadSeleccionada.nombreCiudad : ''}${localidadSeleccionada.nombreProvincia ? ' - ' + localidadSeleccionada.nombreProvincia : ''}` 
                  : (formData.codigoCiudad ? String(formData.codigoCiudad) : '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenLocalidadSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar localidad"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="email" 
                label="Email" 
                value={formData.email || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="telefono1" 
                label="Teléfono 1" 
                value={formData.telefono1 || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="telefono2" 
                label="Teléfono 2" 
                value={formData.telefono2 || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="fax" 
                label="Fax" 
                value={formData.fax || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="contacto" 
                label="Contacto" 
                value={formData.contacto || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            </Grid>
          )}

          {/* Pestaña Comercial */}
          {currentTab === 2 && (
            <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Forma de Pago</InputLabel>
                <Select
                  name="condVta"
                  value={cleanString(formData.condVta) || 'CTA.CTE.'}
                  label="Forma de Pago"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, condVta: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="CTA.CTE.">CTA.CTE.</MenuItem>
                  <MenuItem value="EFECTIVO">EFECTIVO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="plazoEntrega" 
                label="Plazo Entrega (días)" 
                type="number" 
                value={formData.plazoEntrega ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="descuento" 
                label="Descuento %" 
                type="number" 
                value={formData.descuento ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="codigoRuta"
                label="Código de Ruta (o Zona)"
                value={rutaSeleccionada 
                  ? `${rutaSeleccionada.codigoRuta || ''}${rutaSeleccionada.nombreRuta?.trim() ? ' - ' + rutaSeleccionada.nombreRuta.trim() : ''}` 
                  : (formData.codigoRuta ? String(formData.codigoRuta) : '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenRutaSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar ruta"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Sit.Imp.Gan.</InputLabel>
                <Select
                  name="sitIB"
                  value={formData.sitIB || ''}
                  label="Sit.Imp.Gan."
                  onChange={(e) => setFormData(prev => ({ ...prev, sitIB: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="1">Inscripto</MenuItem>
                  <MenuItem value="2">No Inscripto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel 
                control={
                  <Switch 
                    checked={!!formData.esPersHumSucIndig} 
                    onChange={handleSwitch} 
                    name="esPersHumSucIndig"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#1976d2',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                      },
                    }}
                  />
                } 
                label="No inscripto: Es Pers Hum Suc Ind (Imp.Gan.)" 
              />
            </Grid>
            </Grid>
          )}

          {/* Pestaña Extras */}
          {currentTab === 3 && (
            <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField 
                name="fechaAlta" 
                label="Fecha Alta" 
                type="date" 
                value={formData.fechaAlta ? String(formData.fechaAlta).slice(0,10) : ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal" 
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="fechaBaja" 
                label="Fecha Baja" 
                type="date" 
                value={formData.fechaBaja ? String(formData.fechaBaja).slice(0,10) : ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal" 
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                name="notas" 
                label="Notas" 
                value={formData.notas || ''} 
                onChange={handleChange} 
                fullWidth 
                multiline 
                rows={3} 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            </Grid>
          )}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={saving}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {saving ? <CircularProgress size={24} /> : 'Guardar'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/compras/proveedores')} 
              disabled={saving}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Cancelar
            </Button>
          </Box>
        </form>
      </Card>

      <LocalidadSearch
        open={openLocalidadSearch}
        onClose={() => setOpenLocalidadSearch(false)}
        onSelect={handleSelectLocalidad}
      />

      <RutaSearch
        open={openRutaSearch}
        onClose={() => setOpenRutaSearch(false)}
        onSelect={handleSelectRuta}
      />

      <ImputableSearch
        open={openImputableSearch}
        onClose={() => setOpenImputableSearch(false)}
        onSelect={handleSelectImputable}
      />

      <ImputableSearch
        open={openImputableComprasSearch}
        onClose={() => setOpenImputableComprasSearch(false)}
        onSelect={handleSelectImputableCompras}
      />

      <Dialog
        open={openRamoProvDialog}
        onClose={() => {
          setOpenRamoProvDialog(false);
          setRamoProvSeleccionado('');
        }}
        aria-labelledby="ramo-prov-dialog-title"
        aria-describedby="ramo-prov-dialog-description"
      >
        <DialogTitle id="ramo-prov-dialog-title">
          ¿Heredar números de cuenta?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="ramo-prov-dialog-description">
            ¿Desea heredar los números de cuenta (Nro Cuenta - Ctas x Pagar y Nro Cuenta - Compras) del ramo prov seleccionado?
            Los valores actuales serán reemplazados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handleConfirmarRamoProv(false)}
            color="inherit"
          >
            No heredar
          </Button>
          <Button 
            onClick={() => handleConfirmarRamoProv(true)}
            color="primary"
            variant="contained"
            autoFocus
          >
            Sí, heredar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProveedorForm;


